/**
 * ImpactBadge — subtle inline indicator showing which downstream
 * artefacts will be invalidated by the current session's config edits.
 * Sprint P37y · Sprint E.
 *
 * Consumes /api/projects/{pid}/configuration/impact-preview which
 * accepts a list of dotted `section.field` paths and returns the
 * severity-ranked list of impacted artefacts + a
 * `requires_confirmation` flag.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Warning, CheckCircle, Info, ArrowClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const TONE = {
  major:    "border-rose-200 bg-rose-50 text-rose-800",
  minor:    "border-amber-200 bg-amber-50 text-amber-800",
  cosmetic: "border-slate-200 bg-slate-50 text-slate-600",
};

export default function ImpactBadge({ pid, changedFields, onConfirm }) {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!pid || !changedFields || changedFields.length === 0) {
      setImpact(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api.post(`/projects/${pid}/configuration/impact-preview`, {
      changed_fields: changedFields,
    }).then((r) => { if (!cancelled) setImpact(r.data); })
      .catch(() => { if (!cancelled) setImpact(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: we key on the joined field-list identity, not the (possibly re-referenced) array itself, to avoid a re-request on every render when a new array with the same contents is passed in
  }, [pid, changedFields.join("|")]);

  const artifacts = impact?.impacted_artifacts || [];
  const majorCount = artifacts.filter((a) => a.severity === "major").length;

  if (!changedFields.length) return null;

  return (
    <div className="rounded-md border border-brand-blue/20 bg-brand-blue/5 p-3" data-testid="impact-badge">
      <header className="flex items-center gap-2 mb-2">
        {loading ? <ArrowClockwise size={12} className="animate-spin text-brand-blue" /> :
                    majorCount > 0 ? <Warning size={12} className="text-rose-600" weight="fill" /> :
                                        <Info size={12} className="text-brand-blue" />}
        <div className="text-[11.5px] font-semibold text-slate-900">
          {loading ? "Analysing impact…"
             : majorCount > 0 ? `${majorCount} major downstream artefact${majorCount === 1 ? "" : "s"} will need regeneration`
             : "No major regeneration required"}
        </div>
        <span className="ml-auto text-[10px] text-slate-500 font-mono">{changedFields.length} field change{changedFields.length === 1 ? "" : "s"}</span>
      </header>
      {artifacts.length > 0 && (
        <ul className="space-y-1">
          {artifacts.slice(0, 6).map((a, i) => (
            <li key={i} className="flex items-center gap-2 text-[10.5px]"
                 data-testid={`impact-artifact-${a.kind}`}>
              <span className={`inline-flex items-center px-1 py-0.5 rounded-sm border text-[9px] uppercase tracking-widest w-16 justify-center ${TONE[a.severity] || TONE.minor}`}>
                {a.severity}
              </span>
              <span className="text-slate-700 flex-1">{a.label}</span>
              <span className="text-slate-500 font-mono truncate max-w-[50%]">
                {(a.trigger_fields || []).slice(0, 3).join(", ")}
                {(a.trigger_fields || []).length > 3 ? " …" : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
      {onConfirm && impact?.requires_confirmation && (
        <div className="mt-2 pt-2 border-t border-brand-blue/20 flex items-center gap-2">
          <div className="text-[10.5px] text-slate-600 flex-1">
            Approved outputs (CPR, block model, gallery) are preserved. Regeneration is only triggered when you confirm.
          </div>
          <Button size="sm" onClick={onConfirm}
                     data-testid="btn-confirm-regenerate"
                     className="bg-brand-blue hover:bg-brand-blue/90 text-white text-[11px]">
            Confirm & Regenerate
          </Button>
        </div>
      )}
    </div>
  );
}
